import Modal from 'react-modal';
import { ReactNode, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePlayerLayout } from 'lib/playerLayout';
import { BottomSheet } from 'components/mobile/BottomSheet/BottomSheet';
import { VipFooter, VipHeader } from 'components/mobile/VipChrome/VipChrome';
import { MatchInsights, type Tab } from './MatchInsights';
import styles from './MatchInsightsModal.module.css';

interface Props {
  fi: number | string;
  onClose: () => void;
  /** Our odds of a match in play, under its Live and Video tabs. */
  liveOdds?: ReactNode;
  /** Opens on this tab (the league's table from a league page). */
  initialTab?: Tab;
  /** VIP: the match's odds in its top card, to bet while reading. */
  heroOdds?: ReactNode;
}

/** A match's statistics in a window (full screen on a phone); in the modern
 *  layout a sheet from the bottom, the page still showing above it. */
export const MatchInsightsModal = ({
  fi,
  onClose,
  liveOdds,
  initialTab,
  heroOdds,
}: Props) => {
  const { t } = useTranslation();
  const layout = usePlayerLayout();
  const modern = layout === 'modern';
  // The sheet re-binds Esc and the scroll lock when `close` changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const close = useCallback(onClose, []);

  // Classic and VIP: the page behind stands still while this one is open, as
  // under the modern sheet. Else a phone (iPhone) scrolled the page behind and
  // this one stood: only the scroll bar moved (owner, 2026-11-03).
  useEffect(() => {
    if (modern) return;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [modern]);

  if (modern) {
    return (
      <BottomSheet tall close={close}>
        <MatchInsights fi={fi} liveOdds={liveOdds} initialTab={initialTab} />
      </BottomSheet>
    );
  }

  // VIP: an ordinary page with the app's own bars, no Close bar.
  if (layout === 'vip') {
    return (
      <Modal
        isOpen
        ariaHideApp={false}
        onRequestClose={onClose}
        className={styles.vipPage}
        overlayClassName={styles.vipOverlay}
      >
        <VipHeader title={t('match_statistics_label')} onBack={onClose} />
        <div className={styles.vipBody}>
          <MatchInsights
            fi={fi}
            liveOdds={liveOdds}
            initialTab={initialTab}
            heroOdds={heroOdds}
          />
        </div>
        <VipFooter />
      </Modal>
    );
  }

  return (
    <Modal
      isOpen
      ariaHideApp={false}
      onRequestClose={onClose}
      className={styles.modal}
      overlayClassName={styles.overlay}
    >
      <button type="button" className={styles.backBar} onClick={onClose}>
        <span className={styles.backIcon} aria-hidden="true" />
        {t('close_label')}
      </button>
      <div className={styles.body}>
        <MatchInsights fi={fi} liveOdds={liveOdds} initialTab={initialTab} />
      </div>
    </Modal>
  );
};
